import type { HTMLAttributes, ReactNode } from 'react';

import { faHandHoldingBox, faTruck, faWarehouse } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { clsx } from 'clsx';

import { getEnvironmentVariable } from '@spacefill/shared/src/utils/GetEnvVar';

import type { User } from '../../../types/user';
import { cn } from '../../lib/utils';
import { CompanyLogo } from '../CompanyLogo';
import { UserFullname } from '../UserFullname';

enum AvatarColor {
    lime = 'bg-lime-200',
    emerald = 'bg-emerald-200',
    sky = 'bg-sky-200',
    blue = 'bg-blue-200',
    violet = 'bg-violet-200',
    fuchsia = 'bg-fuchsia-200',
    rose = 'bg-rose-200',
    orange = 'bg-orange-200',
    yellow = 'bg-yellow-200',
}

export interface AvatarProps {
    variant?: keyof typeof AvatarColor;
    user?: Pick<User, 'id' | 'firstName' | 'lastName' | 'role' | 'logisticProvider' | 'customer' | 'deletedAt'>;
    hideName?: boolean;
    showAsCompany?: boolean;
    additionalTextComponent?: ReactNode;
    className?: string;
    dataTestid?: string;
    htmlAttributes?: HTMLAttributes<HTMLDivElement>;
}

export const Avatar = ({
    variant,
    user,
    hideName = false,
    showAsCompany = false,
    additionalTextComponent,
    className,
    dataTestid,
    htmlAttributes,
}: AvatarProps) => {
    const attachmentsUrl = getEnvironmentVariable('attachmentsUrl');
    if (!user) {
        return null;
    }

    const fullName =
        user.firstName && user.lastName ? (
            <UserFullname
                firstName={user.firstName}
                lastName={user.lastName}
                isDeleted={!!user.deletedAt}
            />
        ) : null;

    const initials = user.role === 'ADMIN' ? 'SF' : `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`;
    const entity = user.customer?.company || user.logisticProvider?.company;
    const entityUrl = user.customer?.logoUrl || user.logisticProvider?.logoUrl;

    return (
        <div
            data-testid={dataTestid}
            data-name={`${user?.firstName || ''}${user?.lastName || ''}`}
            className={cn('flex items-center gap-2 font-medium text-sm text-gray-800', className)}
            data-component-name='Avatar'
        >
            <div>
                {showAsCompany && (user.customer || user.logisticProvider) ? (
                    entityUrl ? (
                        <CompanyLogo
                            logoUrl={`${attachmentsUrl}/${entityUrl}`}
                            variant='small'
                        />
                    ) : (
                        <div className='flex size-7 items-center justify-center rounded-full text-xs font-medium uppercase bg-gray-200'>
                            <FontAwesomeIcon
                                icon={
                                    user.customer?.company
                                        ? faHandHoldingBox
                                        : user.logisticProvider?.company
                                          ? faWarehouse
                                          : faTruck
                                }
                            />
                        </div>
                    )
                ) : (
                    <span
                        className={clsx(
                            'flex size-7 items-center justify-center rounded-full text-xs font-medium uppercase',
                            user.deletedAt
                                ? 'bg-gray-100 text-gray-500'
                                : user.role === 'ADMIN'
                                  ? 'bg-[#1BD292]'
                                  : AvatarColor[variant ? variant : getVariantFromUserId(user.id!)]
                        )}
                        data-testid={dataTestid ? `${dataTestid}-icon` : ''}
                        {...htmlAttributes}
                    >
                        {initials || '--'}
                    </span>
                )}
            </div>

            {!hideName && (
                <div>
                    <div
                        className='flex items-center gap-2'
                        data-testid={`${dataTestid}-name`}
                    >
                        <div>{user.role === 'ADMIN' ? <>Spacefill</> : <>{showAsCompany ? entity : fullName}</>}</div>
                        {additionalTextComponent}
                    </div>

                    {entity && !showAsCompany && user.role !== 'ADMIN' && (
                        <p className='text-xs text-gray-500'>{entity}</p>
                    )}
                </div>
            )}
        </div>
    );
};

function simpleHash(str: string) {
    return str.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
}

function getVariantFromUserId(userId: string | null): keyof typeof AvatarColor {
    const hash = simpleHash(userId || 'noUserId');
    const variantKeys = Object.keys(AvatarColor);
    const index = hash % variantKeys.length;
    return variantKeys[index] as keyof typeof AvatarColor;
}
